/**
 * Générateur — Placer sur une demi-droite graduée un point dont l'abscisse est un nombre décimal
 * Attendu ID : 602 | Niveau : 6ème | Domaine : NC
 *
 * Types générés : placer_points (3 templates)
 * Labels de référence :
 *   - Placer des points (dixièmes) sur demi-droite graduée
 *   - Placer des points (centièmes) sur demi-droite graduée
 *   - Placer des points (pas varié) sur demi-droite graduée
 * Variantes estimées : >1200
 */

import {
    randomChoice, randomChoiceMultiple, randInt, shuffle,
    basePayload,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 602;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Pools de données ───
const LETTRES_POINTS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];

// ─── Helpers spécifiques ───

/** Formate un nombre en virgule française */
const toFr = (n) => {
    const s = Number(n.toFixed(3)).toString();
    return s.replace('.', ',');
};

/** Arrondi propre pour éviter les erreurs de flottants */
const round = (n, decimals) => {
    const factor = Math.pow(10, decimals);
    return Math.round(n * factor) / factor;
};

/**
 * Génère N abscisses distinctes qui tombent sur les graduations de la droite,
 * en évitant start et end et les valeurs entières si avoidIntegers est false.
 */
const generatePoints = (start, end, step, n, decimals) => {
    // Nombre de graduations possibles (excluant start et end)
    const totalSteps = Math.round((end - start) / step);
    const candidates = [];
    for (let i = 1; i < totalSteps; i++) {
        candidates.push(round(start + i * step, decimals));
    }
    if (candidates.length < n) return candidates.slice(0, n);
    return randomChoiceMultiple(candidates, n).sort((a, b) => a - b);
};

/**
 * Construit un payload placer_points complet.
 */
const makePlacerPoints = (start, end, step, points, labels, label, reperes = []) => {
    const bonneReponse = {};
    labels.forEach((lbl, i) => {
        bonneReponse[lbl] = toFr(points[i]);
    });

    const pointsList = labels.map((lbl, i) => `${lbl}(${toFr(points[i])})`).join(', ');
    const numberline = { start, end, step };
    if (reperes.length > 0) numberline.reperes = reperes;

    return basePayload('association',
        `<p>Place les points ${pointsList} sur la demi-droite graduée.</p>`,
        {
            subType: 'placer_points',
            numberline,
            pointsToPlace: labels,
            bonne_reponse: JSON.stringify(bonneReponse),
        },
        opts(label),
    );
};

// ─── Labels ───
const L1 = 'Placer des points (dixièmes) sur demi-droite graduée';
const L2 = 'Placer des points (centièmes) sur demi-droite graduée';
const L3 = 'Placer des points (pas varié) sur demi-droite graduée';

// ─── Générateurs par template ───

/**
 * Template 1 : Dixièmes — step = 0.1, droite de 2 ou 3 unités
 * Inclut parfois un entier parmi les points pour piéger.
 */
const genPlacerDixiemes = () => {
    const labels = randomChoiceMultiple(LETTRES_POINTS, 3);
    const startInt = randInt(0, 15);
    const span = randomChoice([2, 3]);
    const start = startInt;
    const end = startInt + span;
    const step = 0.1;

    const points = generatePoints(start, end, step, 3, 1);

    // Parfois (1 chance sur 3) remplacer un point par un entier
    if (Math.random() < 0.33 && points.length === 3) {
        const entier = randInt(start + 1, end - 1);
        if (!points.includes(entier)) {
            points[randInt(0, 2)] = entier;
            points.sort((a, b) => a - b);
        }
    }

    return makePlacerPoints(start, end, step, points, labels, L1);
};

/**
 * Template 2 : Centièmes — step = 0.01, droite de 0.1 unité
 * Ex: [5,4 ; 5,5] ou [0 ; 0,1] ou [2,9 ; 3,0]
 */
const genPlacerCentiemes = () => {
    const labels = randomChoiceMultiple(LETTRES_POINTS, 3);

    // Bases possibles pour les centièmes
    const bases = [
        { start: 0, end: 0.1 },
        { start: 0.1, end: 0.2 },
        { start: 0.9, end: 1 },
        { start: 1.5, end: 1.6 },
        { start: 2.9, end: 3 },
        { start: 5.4, end: 5.5 },
        { start: 6.41, end: 6.51 },
        { start: 7.8, end: 7.9 },
        { start: 9.9, end: 10 },
    ];
    // On arrondit end à start+0.1 pour être cohérent
    const base = randomChoice(bases);
    const start = base.start;
    const end = round(start + 0.1, 2);
    const step = 0.01;

    const points = generatePoints(start, end, step, 3, 3);

    return makePlacerPoints(start, end, step, points, labels, L2);
};

/**
 * Template 3 : Pas varié — step non-standard, droite avec repères labellisés.
 * L'élève déduit le pas en divisant l'écart entre 2 repères par le nombre
 * de graduations entre eux (3 à 5 graduations → calcul mental accessible).
 *
 * Algo :
 *  1. Choisir un pas et l'écart entre repères (table PAS_VARIE_CONFIGS)
 *  2. Choisir le nombre d'intervalles entre repères (3 à 5)
 *  3. Calculer start, end, positions des repères intermédiaires
 *  4. Générer 3 points sur les graduations, hors repères
 */
const PAS_VARIE_CONFIGS = [
    // { step, gap (écart entre repères), gradsBetween (graduations entre 2 repères), decimals }
    { step: 0.2,  gap: 1.0, gradsBetween: 5, decimals: 1 },
    { step: 0.25, gap: 1.0, gradsBetween: 4, decimals: 2 },
    { step: 0.3,  gap: 1.2, gradsBetween: 4, decimals: 1 },
    { step: 0.3,  gap: 1.5, gradsBetween: 5, decimals: 1 },
    { step: 0.4,  gap: 1.6, gradsBetween: 4, decimals: 1 },
    { step: 0.4,  gap: 2.0, gradsBetween: 5, decimals: 1 },
    { step: 0.5,  gap: 2.0, gradsBetween: 4, decimals: 1 },
    { step: 0.75, gap: 3.0, gradsBetween: 4, decimals: 2 },
];

const genPlacerPasVarie = () => {
    const labels = randomChoiceMultiple(LETTRES_POINTS, 3);
    const config = randomChoice(PAS_VARIE_CONFIGS);
    const numGaps = randomChoice([3, 4, 5]);
    // Le composant élève snappe sur la grille ABSOLUE (round(v/step)×step,
    // multiples du pas depuis 0) : start doit être un multiple du pas, sinon
    // tous les points sont inatteignables (ex. start=1 avec pas 0,75).
    const startsAlignes = [];
    for (let s = 0; s <= 10; s++) {
        if (Math.abs(Math.round(s / config.step) - s / config.step) < 1e-9) startsAlignes.push(s);
    }
    const start = randomChoice(startsAlignes);
    const end = round(start + config.gap * numGaps, config.decimals);

    // Repères labellisés intermédiaires (start/end gérés par showStart/showEnd)
    const reperes = [];
    for (let i = 1; i < numGaps; i++) {
        reperes.push(round(start + config.gap * i, config.decimals));
    }

    // Exclure les positions des repères + bornes pour les points à placer
    const excluded = new Set(
        [start, end, ...reperes].map(v => round(v, config.decimals).toString()),
    );
    const candidates = [];
    const totalSteps = Math.round((end - start) / config.step);
    for (let i = 1; i < totalSteps; i++) {
        const val = round(start + i * config.step, config.decimals);
        if (!excluded.has(val.toString())) {
            candidates.push(val);
        }
    }

    const points = candidates.length >= 3
        ? randomChoiceMultiple(candidates, 3).sort((a, b) => a - b)
        : candidates.slice(0, Math.min(3, candidates.length));

    return makePlacerPoints(start, end, config.step, points, labels, L3, reperes);
};

// ─── Registre ───
const GENERATORS = {
    placer_dixiemes_placer_points: genPlacerDixiemes,
    placer_centiemes_placer_points: genPlacerCentiemes,
    placer_pas_varie_placer_points: genPlacerPasVarie,
};

// ─── Catégories (obligatoire) ───
const FORMAT_LABELS = {
    placer_points: 'Placer',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'placer_dixiemes', label: L1, formatsList: buildFormats('placer_dixiemes') },
    { id: 'placer_centiemes', label: L2, formatsList: buildFormats('placer_centiemes') },
    { id: 'placer_pas_varie', label: L3, formatsList: buildFormats('placer_pas_varie') },
];

// ─── Point d'entrée (obligatoire) ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
